/**
 * The frame every portal screen sits in.
 *
 * Its own file rather than a export from `Portal.tsx`, because the portal is now four screens
 * across three files — signing in, the account, a purchase, a statement — and every one of them
 * needs the frame. Kept in `Portal.tsx` it would have meant the sign-in screen importing the
 * account screen importing the sign-in screen back, which is a cycle that works until the day it
 * does not.
 *
 * The tone is the one the whole portal is written in: the reader does not work for the agency,
 * may be on a phone, and is here to answer one question about their own money.
 *
 * ---
 *
 * **Whose header this is, and why it changed.**
 *
 * It used to lead with our mark and our wordmark, with the agency's name set small and grey
 * beside them. That is the wrong way round once you ask who the reader is. A tenant opening an
 * invoice here has no relationship with us at all: they rent from an agency, the invoice is the
 * agency's, the money goes to the agency, and the person they will ring about it works there.
 * A document whose largest piece of branding belongs to a company the reader has never heard of
 * reads as a company that has taken over their agency's post.
 *
 * So where the agency is known, the header is theirs — their mark if they have uploaded one,
 * their name if they have not — and our identity moves to the footer, beside the copyright line
 * that already names us. Where the agency is *not* known, which is every screen before a session
 * exists, the header stays ours: that door is genuinely ours, and a sign-in page that named no
 * one would be worse than one that names us.
 *
 * **A missing mark is not a gap.** `AgencyMark` draws the name treatment and nothing else when
 * there is no logo, and tears the picture out silently if it fails to load, so the common case —
 * an agency that has never uploaded one — looks exactly as deliberate as the uncommon one.
 *
 * **Neither the header nor the footer prints.** Two portal screens are printable documents that
 * carry their own letterhead inside `.printable`; a header left in the flow reserves space at the
 * top of the sheet for something the print rules have already made invisible, which is how a
 * statement acquires a second page with nothing on it.
 */

import type { ReactNode } from "react";
import { AgencyMark } from "@/components/agency-mark";
import { Wordmark } from "@/components/wordmark";
import { Copyright } from "@/components/copyright";
import type { PortalMe } from "@/lib/portal";

export function PortalShell({ children, agency }: { children: ReactNode; agency?: PortalMe["agency"] }) {
  return (
    <div className="min-h-screen bg-ink-50">
      <header className="no-print border-b border-ink-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4">
          {/* Whose books these are. A tenant of two agencies must be able to tell at a glance which
              one they are looking at, and the answer is the first thing they should see. */}
          {agency ? (
            <AgencyMark name={agency.name} logoUrl={agency.logoUrl} size="header" />
          ) : (
            <div className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="" width={28} height={28} />
              <Wordmark className="text-base" />
            </div>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-8">{children}</main>
      <footer className="no-print mx-auto max-w-3xl space-y-1 px-5 pb-10 text-xs text-ink-400">
        {/* Ours, and small, and after theirs. Named rather than dropped: somebody being asked for
            money is entitled to know what software sent the request. */}
        {agency ? (
          <p className="flex items-center gap-1.5">
            <img src="/logo.svg" alt="" width={14} height={14} />
            Sent through <Wordmark className="text-xs" />
          </p>
        ) : null}
        <Copyright />
      </footer>
    </div>
  );
}
